import { useInfiniteQuery, useQuery } from "react-query";
import {
	fetchCommand,
	fetchCommandActivityLogsPaginated,
	fetchCommandsPaginated,
	fetchCommandSupplyOrder,
	fetchCommandTickets,
	fetchCommandUPSTracking,
} from "../queries/command.queries";
import type { PaginatedFilter } from "../types/axios.types";

export const useCommandsPaginated = ({
	filters: prefilters,
}: {
	filters?: PaginatedFilter;
}) => {
	return (
		page?: number,
		size?: number,
		search?: string,
		filters?: PaginatedFilter,
		sortBy?: string[],
	) => {
		const computedFilters = { ...prefilters, ...filters };
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const query = useQuery(
			["commands-paginated", page, size, search, computedFilters, sortBy],
			() => {
				try {
					return fetchCommandsPaginated(
						page,
						size,
						search,
						computedFilters,
						sortBy,
					);
				} catch (err) {
					return null;
				}
			},
			{ keepPreviousData: true },
		);
		return query?.data;
	};
};

export const useCenterCommands = (id: string | null | undefined) => {
	const query = useQuery(
		["commands", "center", id],
		() => {
			if (!id) return null;
			return fetchCommandsPaginated(
				1,
				100,
				undefined,
				{
					center: id,
					status: "IN_PROGRESS",
				},
				undefined,
			);
		},
		{ keepPreviousData: true, enabled: !!id },
	);
	return query?.data;
};

export const useInfiniteCommands = ({
	search,
	filters,
	sortBy,
}: {
	search?: string;
	filters?: PaginatedFilter;
	sortBy?: string[];
}) => {
	const computedSortBy =
		sortBy && sortBy.length > 0 ? sortBy : ["expectedShippingDate"];
	return useInfiniteQuery({
		queryKey: ["commands", search, filters, computedSortBy],
		queryFn: ({ pageParam }) => {
			return fetchCommandsPaginated(
				pageParam,
				10,
				search,
				filters,
				computedSortBy,
			);
		},
		getNextPageParam: (data) => {
			const nextPage = data.meta.currentPage + 1;

			if (data.meta.lastPage <= nextPage) return undefined;
			return nextPage;
		},
	});
};

export const useCommandActivityLogsPaginated = ({
	id,
}: {
	id: string | number | undefined;
}) => {
	return (
		page?: number,
		size?: number,
		search?: string,
		filters?: PaginatedFilter,
		sortBy?: string[],
	) => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const query = useQuery(
			["commands-logs-paginated", id, page, size, search, filters, sortBy],
			() => {
				try {
					return fetchCommandActivityLogsPaginated(
						id,
						page,
						size,
						search,
						filters,
						sortBy,
					);
				} catch (err) {
					return null;
				}
			},
			{ keepPreviousData: true },
		);
		return query?.data;
	};
};

export const useCommand = (id: number | string | null) => {
	return useQuery(
		["command", id],
		() => {
			try {
				return fetchCommand(id);
			} catch (err) {
				return null;
			}
		},
		{
			enabled: !!id,
		},
	);
};

export const useCommandSupplyOrder = (id: number | string | null) => {
	return useQuery(["command-supply-order", id], () => {
		try {
			return fetchCommandSupplyOrder(id);
		} catch (err) {
			return null;
		}
	});
};

export const useCommandTickets = (id: number | string | undefined) => {
	const res = useQuery(["command", "tickets", id], () => {
		try {
			return fetchCommandTickets(id);
		} catch (err) {
			return null;
		}
	});
	const tickets = res.data;

	return { tickets };
};

export const useCommandUPSTracking = (id?: number | string | null) => {
	return useQuery(["command", id, "tracking"], () => {
		try {
			return fetchCommandUPSTracking(id);
		} catch (err) {
			return null;
		}
	});
};
